// Republic Atlas - Router Configuration

import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Layout
import MainLayout from '@/components/layouts/MainLayout';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button, Card, CardContent } from '@/components/ui';

// Route Error Component
function RouteErrorBoundary() {
  const error = useRouteError() as Error;
  
  console.error('Route error:', error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Error</h1>
          <p className="text-gray-600 mb-6">
            {error?.message || 'An unexpected error occurred while loading this page.'}
          </p>
          <Button onClick={() => window.location.href = '/'} variant="primary">
            Go Home
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/HomePage'));
const ElectionsPage = lazy(() => import('@/pages/ElectionsPage'));
const OfficePage = lazy(() => import('@/pages/OfficePage'));
const CandidatePage = lazy(() => import('@/pages/CandidatePage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const SubscriptionSuccessPage = lazy(() => import('@/pages/SubscriptionSuccessPage'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Account Page
const AccountPage = lazy(() => import('@/pages/AccountPage'));

// Legal & Info Pages
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const APIDocsPage = lazy(() => import('@/pages/APIDocsPage'));

// Page wrapper with suspense and error boundary
function PageLoader({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

// Router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <HomePage />
          </PageLoader>
        ),
      },
      {
        path: 'elections',
        element: (
          <PageLoader>
            <ElectionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'office/:id',
        element: (
          <PageLoader>
            <OfficePage />
          </PageLoader>
        ),
      },
      {
        path: 'candidate/:id',
        element: (
          <PageLoader>
            <CandidatePage />
          </PageLoader>
        ),
      },
      {
        path: 'search',
        element: (
          <PageLoader>
            <SearchPage />
          </PageLoader>
        ),
      },
      {
        path: 'login',
        element: (
          <PageLoader>
            <LoginPage />
          </PageLoader>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <PageLoader>
            <DashboardPage />
          </PageLoader>
        ),
      },
      {
        path: 'pricing',
        element: (
          <PageLoader>
            <PricingPage />
          </PageLoader>
        ),
      },
      {
        path: 'subscription/success',
        element: (
          <PageLoader>
            <SubscriptionSuccessPage />
          </PageLoader>
        ),
      },
      {
        path: 'account',
        element: (
          <PageLoader>
            <AccountPage />
          </PageLoader>
        ),
      },
      // Legal & Info Pages
      {
        path: 'about',
        element: (
          <PageLoader>
            <AboutPage />
          </PageLoader>
        ),
      },
      {
        path: 'faq',
        element: (
          <PageLoader>
            <FAQPage />
          </PageLoader>
        ),
      },
      {
        path: 'api',
        element: (
          <PageLoader>
            <APIDocsPage />
          </PageLoader>
        ),
      },
      {
        path: 'privacy',
        element: (
          <PageLoader>
            <PrivacyPage />
          </PageLoader>
        ),
      },
      {
        path: 'terms',
        element: (
          <PageLoader>
            <TermsPage />
          </PageLoader>
        ),
      },
      {
        path: 'coming-soon',
        element: (
          <PageLoader>
            <ComingSoonPage />
          </PageLoader>
        ),
      },
      {
        path: '*',
        element: (
          <PageLoader>
            <NotFoundPage />
          </PageLoader>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;
